import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import {
  deleteConversation as apiDeleteConversation,
  updateConversation as apiUpdateConversation,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";

// The subset of a session's fields these actions need. `ProjectConversation`
// satisfies this structurally, but callers whose data comes from elsewhere
// (e.g. the search endpoint's differently-shaped results) can pass a plain
// { id, display_name } instead of adapting their whole session type.
export interface RenameableSession {
  id: string;
  display_name: string;
  agent_id?: string | null;
}

export interface ActionTarget {
  session: RenameableSession;
  action: "share" | "delete";
}

export interface UseSessionActionsOptions {
  // Called after a rename or delete succeeds. The sidebar's list is kept in
  // sync automatically via the shared SWR cache entry below; callers backed
  // by a different data source (e.g. the sessions list page's own search
  // query) use this to trigger their own refetch.
  onMutated?: () => void;
}

// Shared rename/share/delete behavior for a project's sessions, used by both
// the sidebar's recent-sessions list and the full sessions list page so the
// two views can't drift out of sync on how they mutate the same SWR cache
// entry (`projectConversationsPath(projectId)`).
export const useSessionActions = (projectId: string, options?: UseSessionActionsOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useToast();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);

  const startRename = (sessionId: string) => setRenamingId(sessionId);
  const endRename = () => setRenamingId(null);

  const commitRename = (session: RenameableSession, next: string) => {
    const trimmed = next.trim();
    const previous = session.display_name;
    endRename();
    if (!trimmed || trimmed === previous) {
      return;
    }
    const key = projectConversationsPath(projectId);
    const patch = (display_name: string) =>
      mutate(
        key,
        (current: { items: ProjectConversation[] } | undefined) =>
          current && {
            ...current,
            items: current.items.map((s) => (s.id === session.id ? { ...s, display_name } : s)),
          },
        { revalidate: false }
      );
    void patch(trimmed);
    void apiUpdateConversation(session.id, { display_name: trimmed })
      .then(() => options?.onMutated?.())
      .catch(() => {
        void patch(previous);
        void mutate(key);
      });
  };

  const confirmDelete = async () => {
    if (!actionTarget) {
      return;
    }
    const { session } = actionTarget;
    try {
      await apiDeleteConversation(session.id);
      await mutate(projectConversationsPath(projectId));
      options?.onMutated?.();
      if (location.pathname.includes(`/c/${session.id}`)) {
        navigate(nextgenProjectPath(projectId));
      }
    } catch (e) {
      showError("Failed to delete conversation");
      // Rethrow so the confirmation dialog stays open for a retry rather than
      // closing as though the delete had succeeded.
      throw e;
    }
  };

  return {
    renamingId,
    startRename,
    endRename,
    commitRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  };
};
